import { useRef, useState } from "react";
import { Plus, Loader2, UploadCloud, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import type { UploadedSource } from "@/pages/TalkingAvatar/types";

interface TalkingAvatarUploadProps {
  upload: UploadedSource | null;
  isUploading: boolean;
  onAddFile: (file: File) => void;
  variant?: "compact" | "hero";
}

export function TalkingAvatarUpload({
  upload,
  isUploading,
  onAddFile,
  variant = "compact",
}: TalkingAvatarUploadProps) {
  const { t } = useLanguage();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Upload is local-only here — the File is stored client-side and the R2
  // push runs later, inside the hook's `generate*` action (which is what
  // carries the anonymous gate). So this component can open the OS picker
  // for anyone, signed-in or not.
  const openPicker = () => inputRef.current?.click();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onAddFile(file);
    if (inputRef.current) inputRef.current.value = "";
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = Array.from(e.dataTransfer.files).find((f) => f.type.startsWith("image/"));
    if (file) onAddFile(file);
  };

  const isHeroEmpty = variant === "hero" && !upload;

  if (isHeroEmpty) {
    return (
      <>
        <button
          type="button"
          onClick={openPicker}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          disabled={isUploading}
          className={cn(
            "w-full h-full min-h-[260px] md:min-h-[360px] rounded-2xl border-2 border-dashed bg-card/40 flex flex-col items-center justify-center gap-3 px-6 py-8 transition-all duration-200 text-center",
            isDragging ? "border-primary bg-primary/5 scale-[1.01]" : "border-border hover:border-primary/50 hover:bg-card",
            isUploading && "opacity-60 cursor-wait",
          )}
        >
          {isUploading ? (
            <Loader2 className="w-10 h-10 text-muted-foreground animate-spin" />
          ) : (
            <>
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                <UploadCloud className="w-8 h-8 text-primary" />
              </div>
              <div>
                <p className="text-base md:text-lg font-semibold text-foreground">
                  {t.talkingAvatar.heroUploadTitle}
                </p>
                <p className="text-xs md:text-sm text-muted-foreground mt-1">
                  {t.talkingAvatar.heroUploadHint}
                </p>
              </div>
              <p className="text-[11px] text-muted-foreground/80 mt-1">
                {t.talkingAvatar.uploadFormats}
              </p>
            </>
          )}
        </button>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleChange} />
      </>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3">
      <p className="text-sm font-medium text-foreground">{t.talkingAvatar.sourcePhotoLabel}</p>
      {upload ? (
        <>
          <div className="relative w-full max-w-[320px] aspect-[3/4] rounded-2xl overflow-hidden border-2 border-border bg-card shadow-sm">
            <img
              src={upload.previewUrl}
              alt={t.talkingAvatar.sourcePhotoLabel}
              className="absolute inset-0 w-full h-full object-cover"
            />
            {isUploading && (
              <div className="absolute inset-0 bg-background/50 backdrop-blur-[1px] flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-foreground animate-spin" />
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={openPicker}
            disabled={isUploading}
            className={cn(
              "inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border bg-card hover:bg-secondary text-sm font-medium text-foreground transition-colors disabled:opacity-50",
            )}
          >
            <RefreshCw className="w-4 h-4" />
            {t.talkingAvatar.replaceImageCta}
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          disabled={isUploading}
          className={cn(
            "w-full max-w-[320px] aspect-[3/4] rounded-2xl border-2 border-dashed bg-card flex flex-col items-center justify-center gap-2 transition-all duration-200",
            isDragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/40 hover:bg-card",
            isUploading && "opacity-60",
          )}
        >
          {isUploading ? (
            <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
          ) : (
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Plus className="w-5 h-5 text-primary" />
            </div>
          )}
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleChange} />
    </div>
  );
}
